import React, { useMemo } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Platform, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';
import { ButtonFluid, theme } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationCTA = (props: CustomizationCTAProps) => {
  const { variation, cost, calories, quantity, disabled, onPress } = props;

  const { formatNumber, formatMessage } = useIntl();

  const { bottom } = useSafeAreaInsets();
  const safeAreaStyle = useStyle(() => {
    const bottomOffset = Platform.select({
      android: theme.spacing['4'],
      default: 0,
    });

    return { bottom: Math.max(bottom, theme.spacing['4']) + bottomOffset };
  }, [bottom]);

  // ─── Derived Data ────────────────────────────────────────────────────

  const formattedCost = useMemo(() => {
    const formattedValue = formatNumber(cost / 100, {
      style: 'currency',
      currency: 'USD',
    });

    if (quantity > 1) {
      return formatMessage(messages.costPerProduct, { cost: formattedValue });
    }

    return formattedValue;
  }, [formatNumber, cost, quantity, formatMessage]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={[styles.floatingWrapper, safeAreaStyle]}>
      <ButtonFluid.Pressable
        style={styles.pressable}
        disabled={disabled}
        onPress={onPress}
      >
        <ButtonFluid.Label disabled={disabled} sizeMatch={['18']}>
          {variation === 'continue' ? (
            <FormattedMessage {...messages.continueLabel} />
          ) : null}

          {variation === 'add-to-bag' ? (
            <FormattedMessage {...messages.addToBagLabel} />
          ) : null}

          {variation === 'update' ? (
            <FormattedMessage {...messages.updateLabel} />
          ) : null}
        </ButtonFluid.Label>

        <View style={styles.textContainer}>
          <ButtonFluid.Text disabled={disabled} sizeMatch={['16']}>
            <FormattedMessage {...messages.calories} values={{ calories }} />
          </ButtonFluid.Text>

          <ButtonFluid.Text disabled={disabled} sizeMatch={['16']}>
            {formattedCost}
          </ButtonFluid.Text>
        </View>
      </ButtonFluid.Pressable>
    </View>
  );
};

// ─── Constants ──────────────────────────────────────────────────────────────

const CTA_HEIGHT = 64;

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  continueLabel: {
    defaultMessage: 'Continue',
    description: 'Customization | CTA | "Continue" label',
  },
  addToBagLabel: {
    defaultMessage: 'Add to bag',
    description: 'Customization | CTA | "Add to bag" label',
  },
  updateLabel: {
    defaultMessage: 'Update',
    description: 'Customization | CTA | "Update" label',
  },
  calories: {
    defaultMessage: '{calories} cal',
    description: 'Customization | CTA | Calories',
  },
  costPerProduct: {
    defaultMessage: '{cost}/ea',
    description: 'Customization | CTA | Cost per product',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  floatingWrapper: {
    position: 'absolute',
    left: 0,
    right: 0,
    marginHorizontal: theme.spacing['4'],
    height: CTA_HEIGHT,
  },
  pressable: {
    ...theme.elevations[6],
    borderRadius: theme.radius['3xLarge'],
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 0,
    paddingHorizontal: theme.spacing['8'],

    // These overrides are needed for native only, otherwise the text will be cut in the bottom.
    paddingTop: 0,
    paddingBottom: 0,

    // These override the default min height of the `ButtonFluidPressable` so that it stays at the designed height.
    minHeight: CTA_HEIGHT,
    maxHeight: CTA_HEIGHT,
  },
  textContainer: {
    flexDirection: 'row',
    alignItems: 'baseline',
    columnGap: theme.spacing['3'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type CustomizationCTAProps = {
  variation: 'add-to-bag' | 'continue' | 'update';
  cost: number;
  calories: number;
  quantity: number;
  disabled?: boolean;
  onPress: () => void;
};
